import { Box, styled } from "@material-ui/core";
import { isToolUIPart } from "ai";
import { Fragment, useRef } from "react";
import { Link } from "react-router-dom";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import type {
  ApprovalDescriptor,
  ChoiceOption,
  OnboardingUIMessage,
} from "../onboarding-chat";
import type { WatchProgress } from "../use-onboarding-chat";
import { usePacedReveal } from "../use-paced-reveal";
import { useStickToBottom } from "../use-stick-to-bottom";
import { ChoiceGroup } from "./choice-group";
import { ConfirmationCard } from "./confirmation-card";
import { AssistantMessage, TypingIndicator, UserMessage } from "./messages";
import { Milestone } from "./milestone";
import { PayoffCard } from "./payoff-card";
import { SummaryCard } from "./summary-card";
import { ToolActivity } from "./tool-activity";
import { WatchCard } from "./watch-card";

const Scroller = styled("div")({
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  // Scroll anchoring fights content that grows every frame.
  overflowAnchor: "none",
});

const Column = styled(Box)({
  padding: "12px 18px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 14,
});

const JumpButton = styled("button")({
  position: "sticky",
  bottom: 8,
  alignSelf: "center",
  minHeight: 44,
  padding: "0 16px",
  borderRadius: 22,
  cursor: "pointer",
  font: "inherit",
  fontSize: rem(13),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  boxShadow: "0 2px 8px rgba(16,24,38,.12)",
});

const CtaButton = styled(Link)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 44,
  padding: "0 16px",
  borderRadius: 10,
  cursor: "pointer",
  font: "inherit",
  fontSize: rem(14),
  fontWeight: 700,
  color: EPOCUS_WHITE,
  backgroundColor: EPOCUS_PRIMARY,
  textDecoration: "none",
});

export const ConversationView: FC<{
  messages: OnboardingUIMessage[];
  thinking: boolean;
  streaming: boolean;
  watchProgress: Record<string, WatchProgress>;
  onRespondToApproval: (approvalId: string, approved: boolean) => void;
  onRespondToChoices: (options: ChoiceOption[]) => void;
  onCheckRoutingNow: () => void;
}> = ({
  messages,
  thinking,
  streaming,
  watchProgress,
  onRespondToApproval,
  onRespondToChoices,
  onCheckRoutingNow,
}) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { stuck, scrollToBottom } = useStickToBottom(scrollerRef);
  const lastMessageId = messages.at(-1)?.id;

  const activityOrder = new Map<string, number>();
  for (const message of messages) {
    message.parts.forEach((part, index) => {
      if (part.type === "dynamic-tool" || isToolUIPart(part)) {
        activityOrder.set(`${message.id}-${index}`, activityOrder.size);
      }
    });
  }
  const revealedActivity = usePacedReveal(activityOrder.size);

  return (
    <Scroller ref={scrollerRef}>
      <Column role="log" aria-label="Setup conversation">
        {messages.map((message) => {
          const live = message.id === lastMessageId && !streaming;

          return message.parts.map((part, index) => {
            const key = `${message.id}-${index}`;

            if (part.type === "text") {
              return message.role === "user" ? (
                <UserMessage key={key} text={part.text} />
              ) : (
                <AssistantMessage
                  key={key}
                  text={part.text}
                  warm={message.parts[index - 1]?.type === "data-tone"}
                />
              );
            }

            if (part.type === "data-tone") return <Fragment key={key} />;

            if (part.type === "data-summary") {
              return (
                <SummaryCard
                  key={key}
                  title={part.data.title}
                  rows={part.data.rows}
                />
              );
            }

            if (part.type === "data-choices") {
              return (
                <ChoiceGroup
                  key={key}
                  mode={part.data.mode}
                  options={part.data.options}
                  selected={part.data.selected}
                  submitLabel={part.data.submitLabel}
                  live={live}
                  onRespond={onRespondToChoices}
                />
              );
            }

            if (part.type === "data-milestone") {
              return (
                <Milestone
                  key={key}
                  title={part.data.title}
                  detail={part.data.detail}
                />
              );
            }

            if (part.type === "data-payoff") {
              return (
                <PayoffCard
                  key={key}
                  title={part.data.title}
                  steps={part.data.steps}
                  footer={part.data.footer}
                />
              );
            }

            if (part.type === "data-watch") {
              return (
                <WatchCard
                  key={key}
                  instructions={part.data.instructions}
                  progress={part.id ? watchProgress[part.id] : undefined}
                  onCheckNow={onCheckRoutingNow}
                />
              );
            }

            if (part.type === "data-cta") {
              if (!isInternalRoute(part.data.destination)) return null;
              return (
                <CtaButton key={key} to={part.data.destination}>
                  {part.data.label}
                </CtaButton>
              );
            }

            const toolPart =
              part.type === "dynamic-tool"
                ? part
                : isToolUIPart(part)
                  ? part
                  : undefined;

            if (toolPart) {
              if ((activityOrder.get(key) ?? 0) >= revealedActivity) {
                return <Fragment key={key} />;
              }

              const toolName =
                toolPart.type === "dynamic-tool"
                  ? toolPart.toolName
                  : toolPart.type.slice("tool-".length);

              const ask = toolPart.approval
                ? approvalCopy(
                    toolName,
                    toolPart.title,
                    toolPart.approval.descriptor,
                  )
                : undefined;
              const awaitingAnswer = toolPart.state === "approval-requested";

              return (
                <Fragment key={key}>
                  {ask && toolPart.approval && (
                    <ConfirmationCard
                      prompt={ask.prompt}
                      reason={toolPart.approval.requestReason}
                      confirmLabel={ask.confirmLabel}
                      declineLabel={ask.declineLabel}
                      answered={!awaitingAnswer}
                      approved={toolPart.approval.approved}
                      disabled={!awaitingAnswer || !live}
                      onRespond={(approved) =>
                        onRespondToApproval(
                          (toolPart.approval as { id: string }).id,
                          approved,
                        )
                      }
                    />
                  )}
                  {!awaitingAnswer && toolPart.state !== "output-denied" && (
                    <ToolActivity
                      toolName={toolName}
                      title={toolPart.title}
                      state={toolPart.state}
                      input={toolPart.input}
                      output={toolPart.output}
                      errorText={toolPart.errorText}
                    />
                  )}
                </Fragment>
              );
            }

            return <Fragment key={key} />;
          });
        })}

        {thinking && <TypingIndicator />}
        {!stuck && (
          <JumpButton type="button" onClick={scrollToBottom}>
            Jump to latest
          </JumpButton>
        )}
      </Column>
    </Scroller>
  );
};

export const isInternalRoute = (destination: string): boolean =>
  destination.startsWith("/") && !destination.startsWith("//");

const approvalCopy = (
  toolName: string,
  title: string | undefined,
  descriptor: unknown,
): { prompt: string; confirmLabel: string; declineLabel: string } => {
  const supplied = (descriptor ?? {}) as ApprovalDescriptor;
  return {
    prompt: supplied.prompt ?? title ?? `Go ahead with ${toolName}?`,
    confirmLabel: supplied.confirmLabel ?? "Yes, go ahead",
    declineLabel: supplied.declineLabel ?? "Not yet",
  };
};
